import { reminder } from "src/types";

export interface ReminderData {
  patientReminderId: string;
  patientId: string;
  activityType: string;
  targetEntityId: string;
  taskId: string;
  metaData: any;
  startTime: string;
  endTime: string;
  frequency: string;
  customFreqAttributes: {
    days: string[];
    defaultTimes: string[];
  };
  isActive: boolean;
  updatedBy: string;
  referenceId: string;
  maxRetryAttempts: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReminderResponse {
  reminders: ReminderData[];
}

/**
 * Maps activity types from log details screen keys to reminder activity types
 */
export const ACTIVITY_TYPE_MAP: Record<string, string> = {
  "water-logs": "WATER",
  "sleep-logs": "SLEEP",
  "medication-logs": "MEDICATION",
  "diet-logs": "DIET",
  "exercise-logs": "EXERCISE",
  "symptoms-logs": "SYMPTOMS",
};

/**
 * Checks if there are existing reminders for a specific activity type
 * @param reminders - Array of reminder data
 * @param activityType - The activity type to check for
 * @returns The first matching reminder or null if none found
 */
export const getExistingReminder = (
  reminders: ReminderData[] | undefined,
  activityType: string
): ReminderData | null => {
  if (!reminders || !Array.isArray(reminders)) {
    return null;
  }

  return (
    reminders.find(
      (reminder) =>
        reminder.activityType === activityType && !reminder.isDeleted
    ) || null
  );
};

/**
 * Checks if there are existing reminders for a log details screen key
 * @param reminders - Array of reminder data
 * @param logDetailsScreenKey - The log details screen key (e.g., "water-logs")
 * @returns The first matching reminder or null if none found
 */
export const getExistingReminderByLogKey = (
  reminders: ReminderData[] | undefined,
  logDetailsScreenKey: string
): ReminderData | null => {
  const activityType = ACTIVITY_TYPE_MAP[logDetailsScreenKey];
  if (!activityType) {
    return null;
  }

  return getExistingReminder(reminders, activityType);
};

/**
 * Formats reminder data for pre-filling forms
 * @param reminder - The reminder data to format
 * @returns Formatted data for pre-filling
 */
export const formatReminderForForm = (reminder: ReminderData) => {
  return {
    patientReminderId: reminder.patientReminderId,
    activityType: reminder.activityType,
    frequency: reminder.frequency,
    startTime: reminder.startTime,
    endTime: reminder.endTime,
    customFreqAttributes: reminder.customFreqAttributes,
    isActive: reminder.isActive,
  };
};

/**
 * Checks if reminders exist for a specific activity type
 * @param reminders - Array of reminder data
 * @param activityType - The activity type to check for
 * @returns Boolean indicating if reminders exist
 */
export const hasExistingReminders = (
  reminders: ReminderData[] | undefined,
  activityType: string
): boolean => {
  return getExistingReminder(reminders, activityType) !== null;
};

/**
 * Checks if reminders exist for a log details screen key
 * @param reminders - Array of reminder data
 * @param logDetailsScreenKey - The log details screen key
 * @returns Boolean indicating if reminders exist
 */
export const hasExistingRemindersByLogKey = (
  reminders: ReminderData[] | undefined,
  logDetailsScreenKey: string
): boolean => {
  return getExistingReminderByLogKey(reminders, logDetailsScreenKey) !== null;
};
